import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack } from '../components/icons';

// TS port of the source (Falah) app's pages/GoodsReceiptDetail.jsx. This
// views a Goods Receipt (created by ProcurementReceiptForm.tsx) — not to be
// confused with a Revenue Receipt (ReceiptDetail.tsx). Document deletion is
// deliberately not offered here: ProcurementFulfillmentController exposes
// no destroy route for a goods-receipt document (only
// PurchaseRequestController::destroyDocument exists, scoped to purchase
// request quotations while still draft).
const STATUS_LABELS: Record<string, string> = {
  fully_received: 'Fully Received',
  partially_received: 'Partially Received',
  issue_reported: 'Issue Reported',
};

function statusBadgeClass(status: string): string {
  if (status === 'fully_received') return 'badge-normal';
  if (status === 'issue_reported') return 'badge-important';
  return 'badge-neutral';
}

interface GoodsReceiptDocument {
  id: number;
  original_name: string;
  url: string;
}

interface GoodsReceiptRecord {
  id: number;
  invoice_number: string | null;
  received_at: string;
  quantity_received: string | null;
  condition: string | null;
  remarks: string | null;
  status: string;
  received_by: { full_name: string } | null;
  purchase_order: { id: number; po_number: string; vendor_name: string } | null;
  documents: GoodsReceiptDocument[];
}

export function GoodsReceiptDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [gr, setGr] = useState<GoodsReceiptRecord | null>(null);
  const [actionError, setActionError] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refresh = async () => {
    const res = await api.get<GoodsReceiptRecord>(`/goods-receipts/${id}`);
    setGr(res.data);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const pickDocument = () => fileInputRef.current?.click();

  const onDocumentSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const data = new FormData();
    data.append('file', file);
    setActionError('');
    try {
      await api.post(`/goods-receipts/${id}/documents`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Upload failed.');
    }
  };

  if (!gr) return null;

  const po = gr.purchase_order;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/procurement/receiving')} />
        </div>
      </header>

      {actionError && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          {actionError}
        </div>
      )}

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{po?.po_number}</div>
            <span className={`badge ${statusBadgeClass(gr.status)}`}>{STATUS_LABELS[gr.status] ?? gr.status}</span>
            <div className="detail-summary-service">{po?.vendor_name}</div>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Purchase order</span>
            {po ? (
              <Link className="detail-item-value" to={`/procurement/orders/${po.id}`}>
                {po.po_number}
              </Link>
            ) : (
              <span className="detail-item-value empty">Not available</span>
            )}
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Invoice number</span>
            <span className={`detail-item-value${gr.invoice_number ? '' : ' empty'}`}>
              {gr.invoice_number || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Received date</span>
            <span className="detail-item-value">{gr.received_at}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Quantity received</span>
            <span className={`detail-item-value${gr.quantity_received ? '' : ' empty'}`}>
              {gr.quantity_received || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Condition</span>
            <span className={`detail-item-value${gr.condition ? '' : ' empty'}`}>{gr.condition || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Received by</span>
            <span className="detail-item-value">{gr.received_by?.full_name}</span>
          </div>
        </div>

        <div className="detail-item detail-item-full">
          <span className="detail-item-label">Remarks</span>
          {gr.remarks ? (
            <p className="detail-notes">{gr.remarks}</p>
          ) : (
            <span className="detail-item-value empty">Not provided</span>
          )}
        </div>

        <div className="detail-item detail-item-full">
          <span className="detail-item-label">Documents</span>
          <ul className="simple-list">
            {gr.documents?.map((doc) => (
              <li key={doc.id}>
                <a href={doc.url} target="_blank" rel="noreferrer">
                  {doc.original_name}
                </a>
              </li>
            ))}
            {(!gr.documents || gr.documents.length === 0) && <li className="muted">No documents attached yet.</li>}
          </ul>
          <button type="button" className="btn btn-ghost" onClick={pickDocument}>
            + Attach invoice document
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            onChange={onDocumentSelected}
            hidden
          />
        </div>
      </div>
    </>
  );
}
